/** @jsxImportSource @emotion/react */
import React, { Suspense, useMemo, useState, useCallback, useEffect, startTransition } from "react";
import { useParams } from "react-router";
import { css } from "@emotion/react";
import { graphql, useLazyLoadQuery } from "react-relay";
import {
    DndContext,
    DragEndEvent,
    PointerSensor,
    useSensor,
    useSensors,
    closestCenter,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    rectSortingStrategy,
} from "@dnd-kit/sortable";
import { Loading, Flex, useTimeRange, InfoTooltip } from "@phoenix/components";
import { ConnectedTimeRangeSelector } from "@phoenix/components/datetime";
import { useTimeBinScale } from "@phoenix/hooks/useTimeBin";
import { useUTCOffsetMinutes } from "@phoenix/hooks/useUTCOffsetMinutes";
import { ActivityOutline, BarChartOutline, CubeOutline, ClockOutline, AlertCircleOutline, ShieldOutline } from "@phoenix/components/core/icon/Icons";
import { ONE_MONTH_MS } from "@phoenix/constants/timeConstants";
import { useStreamState } from "@phoenix/contexts/StreamStateContext";
import { ErrorBoundary } from "@phoenix/components/exception";
import { TraceTokenCountTimeSeries } from "@phoenix/pages/project/metrics/TraceTokenCountTimeSeries";
import { TraceTokenCostTimeSeries } from "@phoenix/pages/project/metrics/TraceTokenCostTimeSeries";
import { TopModelsByCost } from "@phoenix/pages/project/metrics/TopModelsByCost";
import { TopModelsByToken } from "@phoenix/pages/project/metrics/TopModelsByToken";
import { ModelCostsSummary } from "@phoenix/pages/project/metrics/ModelCostsSummary";
import { TracesSummary } from "@phoenix/pages/project/metrics/TracesSummary";
import { TracesByTimeSummary } from "@phoenix/pages/project/metrics/TracesByTimeSummary";
import { TopUsersByCost } from "@phoenix/pages/project/metrics/TopUsersByCost";
import { TopAgentsByCost } from "@phoenix/pages/project/metrics/TopAgentsByCost";
import { TopUsersByToken } from "@phoenix/pages/project/metrics/TopUsersByToken";
import { TopAgentsByToken } from "@phoenix/pages/project/metrics/TopAgentsByToken";
import { TopKeyAliasesByCost } from "@phoenix/pages/project/metrics/TopKeyAliasesByCost";
import { TopKeyAliasesByToken } from "@phoenix/pages/project/metrics/TopKeyAliasesByToken";
import { costFormatter, intShortFormatter } from "@phoenix/utils/numberFormatUtils";
import { DashboardWidget } from "@phoenix/pages/project/DashboardWidget";
import { ModelUsageSummary } from "@phoenix/pages/project/metrics/ModelUsageSummary";
import { ToolUsageSummary } from "@phoenix/pages/project/metrics/ToolUsageSummary";
import { RetrieverUsageSummary } from "@phoenix/pages/project/metrics/RetrieverUsageSummary";
import { ToolLatencySummary } from "@phoenix/pages/project/metrics/ToolLatencySummary";
import { ToolPerformanceTable } from "@phoenix/pages/project/metrics/ToolPerformanceTable";
import { RetrieverPerformanceTable } from "@phoenix/pages/project/metrics/RetrieverPerformanceTable";
import { QualityDashboard } from "@phoenix/pages/project/metrics/QualityDashboard";
import { LatencyByTimeSummary } from "@phoenix/pages/project/metrics/LatencyByTimeSummary";
import { ErrorsByTimeSummary } from "@phoenix/pages/project/metrics/ErrorsByTimeSummary";
import { PIIUsageSummary } from "@phoenix/pages/project/metrics/PIIUsageSummary";
import { type ChartType } from "@phoenix/pages/project/metrics/ChartTypeRenderer";

import { downloadCSV } from "@phoenix/utils/csvUtils";
import type { ProjectDashboardSummaryPageQuery } from "./__generated__/ProjectDashboardSummaryPageQuery.graphql";

// ─── Types ─────────────────────────────────────────────────────────────────────
type WidgetId =
    | "modelUsage"
    | "tracesSummary"
    | "modelCostsSummary"
    | "tracesByTime"
    | "usersCost"
    | "agentsCost"
    | "keyAliasesCost"
    | "usersToken"
    | "agentsToken"
    | "keyAliasesToken"
    | "costTimeSeries"
    | "tokenTimeSeries"
    | "modelsCost"
    | "modelsToken"
    | "latencyByTime"
    | "errorsByTime"
    | "piiUsage";

interface WidgetMeta {
    id: WidgetId;
    title: string;
    subtitle: string;
    chartType: ChartType;
    supportsTypeSwitch: boolean;
    info?: string;
}

const INITIAL_WIDGETS: WidgetMeta[] = [
    { id: "modelUsage", title: "Model usage", subtitle: "Cost and token trends per model", chartType: "line", supportsTypeSwitch: false, info: "Cost and token trends over time, broken down by individual model names." },
    { id: "modelCostsSummary", title: "Model costs", subtitle: "Breakdown of cost per model", chartType: "vertical_bar", supportsTypeSwitch: false, info: "Total estimated spend per model used in the application." },
    { id: "tracesSummary", title: "Traces", subtitle: "Trace distribution by agent", chartType: "horizontal_bar", supportsTypeSwitch: false, info: "Total number of traces generated by each agent or user." },
    { id: "tracesByTime", title: "Traces by time", subtitle: "Visualizing trace trends", chartType: "line", supportsTypeSwitch: false, info: "The volume of traces recorded over time, helping identify usage spikes or drops." },
    { id: "keyAliasesCost", title: "Top key aliases by cost", subtitle: "Total cost per key alias", chartType: "vertical_bar", supportsTypeSwitch: true, info: "Identifies which API key aliases are incurring the highest costs." },
    { id: "usersCost", title: "Top users by cost", subtitle: "Total cost per username", chartType: "vertical_bar", supportsTypeSwitch: true, info: "Identifies which users are incurring the highest costs." },
    { id: "agentsCost", title: "Top agents by cost", subtitle: "Total cost per agent name", chartType: "vertical_bar", supportsTypeSwitch: true, info: "Identifies which agents are incurring the highest costs." },
    { id: "keyAliasesToken", title: "Top key aliases by token usage", subtitle: "Total tokens per key alias", chartType: "vertical_bar", supportsTypeSwitch: true, info: "Identifies API key aliases with the highest token consumption." },
    { id: "usersToken", title: "Top users by token usage", subtitle: "Total tokens per username", chartType: "vertical_bar", supportsTypeSwitch: true, info: "Identifies users with the highest token consumption." },
    { id: "agentsToken", title: "Top agents by token usage", subtitle: "Total tokens per agent name", chartType: "vertical_bar", supportsTypeSwitch: true, info: "Identifies agents with the highest token consumption." },
    { id: "latencyByTime", title: "Latency", subtitle: "Trace latency trends (p50, p90, p99)", chartType: "line", supportsTypeSwitch: false, info: "Tracks response time distribution over time for all traces." },
    { id: "errorsByTime", title: "Errors", subtitle: "Trace error count and rate trends", chartType: "line", supportsTypeSwitch: false, info: "Visualizes the total volume of errors and the percentage of traces that failed." },
];


// ─── Styles ────────────────────────────────────────────────────────────────────
const pageCSS = css`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding: 24px;
  box-sizing: border-box;
  background: var(--ac-global-background-color-dark);
`;

const headerCSS = css`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
`;

const titleCSS = css`
  font-size: 20px;
  font-weight: 700;
  color: var(--ac-global-text-color-900);
  letter-spacing: -0.3px;
`;

const subtitleCSS = css`
  font-size: 12px;
  color: var(--ac-global-text-color-500);
  margin-top: 2px;
`;

const sectionHeaderCSS = css`
  display: flex;
  margin-top: 32px;
  margin-bottom: 24px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--ac-global-color-grey-100, rgba(0,0,0,0.08));
  gap: 32px;
`;

const sectionTabCSS = (active: boolean) => css`
  font-size: 16px;
  font-weight: ${active ? 700 : 500};
  color: ${active ? "var(--ac-global-color-primary)" : "var(--ac-global-text-color-400)"};
  cursor: pointer;
  position: relative;
  transition: all 0.2s;
  padding: 0 4px 12px 4px;
  
  &::after {
    content: "";
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 3px;
    background: ${active ? "var(--ac-global-color-primary)" : "transparent"};
    border-radius: 3px 3px 0 0;
    transition: all 0.2s;
  }

  &:hover {
    color: var(--ac-global-color-primary);
    opacity: 0.8;
  }
`;

const statGridCSS = css`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 14px;
  margin-bottom: 24px;
  @media (max-width: 1300px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
`;

const statCardCSS = (accent: string) => css`
  position: relative;
  background: linear-gradient(135deg, ${accent}12 0%, var(--ac-global-background-color-dark) 100%);
  border: 1px solid ${accent}30;
  border-top: 4px solid ${accent};
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    background: radial-gradient(circle at top right, ${accent}15, transparent 70%);
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-6px) scale(1.02);
    box-shadow: 0 20px 30px -10px ${accent}30;
    border-color: ${accent}60;
    background: linear-gradient(135deg, ${accent}18 0%, var(--ac-global-background-color-dark) 100%);
  }
`;

const iconContainerCSS = (accent: string) => css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: ${accent};
  color: #ffffff;
  margin-bottom: 12px;
  box-shadow: 0 4px 12px ${accent}40;
  
  & svg {
    width: 22px;
    height: 22px;
    stroke: currentColor;
    stroke-width: 2.5;
    fill: none;
  }
`;

const statLabelCSS = css`
  font-size: 10px;
  font-weight: 700;
  color: var(--ac-global-text-color-600);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
`;

const statValueCSS = css`
  font-size: 28px;
  font-weight: 800;
  color: var(--ac-global-text-color-900);
  line-height: 1.1;
  margin-bottom: 6px;
  letter-spacing: -0.5px;
`;

const statHintCSS = css`
  font-size: 11px;
  color: var(--ac-global-text-color-500);
`;

const widgetGridCSS = css`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding-bottom: 40px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

// ─── Stat Cards ────────────────────────────────────────────────────────────────
function DashboardStatCards({ projectId, timeRange, fetchKey }: { projectId: string; timeRange: TimeRange, fetchKey: any }) {
    const data = useLazyLoadQuery<ProjectDashboardSummaryPageQuery>(
        graphql`
      query ProjectDashboardSummaryPageQuery($projectId: ID!, $timeRange: TimeRange!) {
        project: node(id: $projectId) {
          ... on Project {
            traceCount(timeRange: $timeRange)
            costSummary(timeRange: $timeRange) { 
              total { 
                cost 
                tokens
              } 
            }
            latencyMsP50: latencyMsQuantile(probability: 0.50, timeRange: $timeRange)
            latencyMsP99: latencyMsQuantile(probability: 0.99, timeRange: $timeRange)
          }
        }
      }
    `,
        {
            projectId,
            timeRange: {
                start: timeRange.start.toISOString(),
                end: timeRange.end.toISOString()
            },
        },
        { fetchPolicy: "network-only", fetchKey } // ensure we always hit network when fetchKey changes
    );

    const p = data.project;
    const fmt = (ms: number | null | undefined) => {
        if (ms == null) return "--";
        return ms < 1000 ? `${ms.toFixed(0)}ms` : `${(ms / 1000).toFixed(2)}s`;
    };

    return (
        <div css={statGridCSS}>
            <div css={statCardCSS("#3b82f6")}>
                <Flex direction="row" justifyContent="space-between" alignItems="start">
                    <div css={iconContainerCSS("#3b82f6")}>
                        <BarChartOutline />
                    </div>
                    <InfoTooltip content="The total number of root spans (traces) recorded in the selected time range." />
                </Flex>
                <div css={statLabelCSS}>Total Traces</div>
                <div css={statValueCSS}>{(p?.traceCount ?? 0).toLocaleString()}</div>
                <div css={statHintCSS}>Root span count</div>
            </div>

            <div css={statCardCSS("#10b981")}>
                <Flex direction="row" justifyContent="space-between" alignItems="start">
                    <div css={iconContainerCSS("#10b981")}>
                        <BarChartOutline />
                    </div>
                    <InfoTooltip content="Estimated total cost in USD based on model pricing and token usage." />
                </Flex>
                <div css={statLabelCSS}>Total Cost</div>
                <div css={statValueCSS}>{costFormatter(p?.costSummary?.total?.cost ?? 0)}</div>
                <div css={statHintCSS}>Estimated USD</div>
            </div>

            <div css={statCardCSS("#06b6d4")}>
                <Flex direction="row" justifyContent="space-between" alignItems="start">
                    <div css={iconContainerCSS("#06b6d4")}>
                        <CubeOutline />
                    </div>
                    <InfoTooltip content="Sum of all input (prompt) and output (completion) tokens across all traces." />
                </Flex>
                <div css={statLabelCSS}>Total Tokens</div>
                <div css={statValueCSS}>{intShortFormatter(p?.costSummary?.total?.tokens ?? 0)}</div>
                <div css={statHintCSS}>Input + Output</div>
            </div>

            <div css={statCardCSS("#8b5cf6")}>
                <Flex direction="row" justifyContent="space-between" alignItems="start">
                    <div css={iconContainerCSS("#8b5cf6")}>
                        <ClockOutline />
                    </div>
                    <InfoTooltip content="The median response time. 50% of traces are faster than this value." />
                </Flex>
                <div css={statLabelCSS}>Latency P50</div>
                <div css={statValueCSS}>{fmt(p?.latencyMsP50)}</div>
                <div css={statHintCSS}>Median response time</div>
            </div>

            <div css={statCardCSS("#f59e0b")}>
                <Flex direction="row" justifyContent="space-between" alignItems="start">
                    <div css={iconContainerCSS("#f59e0b")}>
                        <AlertCircleOutline />
                    </div>
                    <InfoTooltip content="99th percentile response time. Only 1% of traces are slower than this value." />
                </Flex>
                <div css={statLabelCSS}>Latency P99</div>
                <div css={statValueCSS}>{fmt(p?.latencyMsP99)}</div>
                <div css={statHintCSS}>99th percentile</div>
            </div>
        </div>
    );
}

// ─── Widget content ────────────────────────────────────────────────────────────
function WidgetContent({
    widgetId,
    widgetTitle,
    chartType,
    projectId,
    timeRange,
    onDataExport,
    fetchKey,
}: {
    widgetId: WidgetId;
    widgetTitle: string;
    chartType: ChartType;
    projectId: string;
    timeRange: TimeRange;
    onDataExport: (id: string, title: string, data: any[]) => void;
    fetchKey: any;
}) {
    switch (widgetId) {
        case "modelUsage":
            return <ModelUsageSummary projectId={projectId} timeRange={timeRange} fetchKey={fetchKey} widgetId={widgetId} widgetTitle={widgetTitle} onDataExport={onDataExport} />;
        case "tracesByTime":
            return <TracesByTimeSummary projectId={projectId} timeRange={timeRange} fetchKey={fetchKey} widgetId={widgetId} widgetTitle={widgetTitle} onDataExport={onDataExport} />;
        case "tracesSummary":
            return <TracesSummary projectId={projectId} timeRange={timeRange} fetchKey={fetchKey} widgetId={widgetId} widgetTitle={widgetTitle} onDataExport={onDataExport} />;
        case "modelCostsSummary":
            return <ModelCostsSummary projectId={projectId} timeRange={timeRange} fetchKey={fetchKey} widgetId={widgetId} widgetTitle={widgetTitle} onDataExport={onDataExport} />;
        case "usersCost":
            return <TopUsersByCost projectId={projectId} timeRange={timeRange} chartType={chartType} fetchKey={fetchKey} widgetId={widgetId} widgetTitle={widgetTitle} onDataExport={onDataExport} />;
        case "agentsCost":
            return <TopAgentsByCost projectId={projectId} timeRange={timeRange} chartType={chartType} fetchKey={fetchKey} widgetId={widgetId} widgetTitle={widgetTitle} onDataExport={onDataExport} />;
        case "keyAliasesCost":
            return <TopKeyAliasesByCost projectId={projectId} timeRange={timeRange} chartType={chartType} fetchKey={fetchKey} widgetId={widgetId} widgetTitle={widgetTitle} onDataExport={onDataExport} />;
        case "usersToken":
            return <TopUsersByToken projectId={projectId} timeRange={timeRange} chartType={chartType} fetchKey={fetchKey} widgetId={widgetId} widgetTitle={widgetTitle} onDataExport={onDataExport} />;
        case "agentsToken":
            return <TopAgentsByToken projectId={projectId} timeRange={timeRange} chartType={chartType} fetchKey={fetchKey} widgetId={widgetId} widgetTitle={widgetTitle} onDataExport={onDataExport} />;
        case "keyAliasesToken":
            return <TopKeyAliasesByToken projectId={projectId} timeRange={timeRange} chartType={chartType} fetchKey={fetchKey} widgetId={widgetId} widgetTitle={widgetTitle} onDataExport={onDataExport} />;
        case "costTimeSeries":
            return <TraceTokenCostTimeSeries projectId={projectId} timeRange={timeRange} chartType={chartType} fetchKey={fetchKey} widgetId={widgetId} widgetTitle={widgetTitle} onDataExport={onDataExport} />;
        case "tokenTimeSeries":
            return <TraceTokenCountTimeSeries projectId={projectId} timeRange={timeRange} chartType={chartType} fetchKey={fetchKey} widgetId={widgetId} widgetTitle={widgetTitle} onDataExport={onDataExport} />;
        case "modelsCost":
            return <TopModelsByCost projectId={projectId} timeRange={timeRange} fetchKey={fetchKey} widgetId={widgetId} widgetTitle={widgetTitle} onDataExport={onDataExport} />;
        case "modelsToken":
            return <TopModelsByToken projectId={projectId} timeRange={timeRange} fetchKey={fetchKey} widgetId={widgetId} widgetTitle={widgetTitle} onDataExport={onDataExport} />;
        case "latencyByTime":
            return <LatencyByTimeSummary projectId={projectId} timeRange={timeRange} fetchKey={fetchKey} widgetId={widgetId} widgetTitle={widgetTitle} onDataExport={onDataExport} />;
        case "errorsByTime":
            return <ErrorsByTimeSummary projectId={projectId} timeRange={timeRange} fetchKey={fetchKey} widgetId={widgetId} widgetTitle={widgetTitle} onDataExport={onDataExport} />;
        case "piiUsage":
            return <PIIUsageSummary projectId={projectId} timeRange={timeRange} fetchKey={fetchKey} widgetId={widgetId} widgetTitle={widgetTitle} onDataExport={onDataExport} />;
        default:
            return null;
    }
}

// ─── Tool Stat Cards ───────────────────────────────────────────────────────────
const toolStatGridCSS = css`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
  @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const toolStatCardCSS = css`
  background: var(--ac-global-background-color-dark);
  border: 1px solid var(--ac-global-color-grey-100, rgba(0,0,0,0.08));
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 166px rgba(0, 0, 0, 0.05);
    border-color: var(--ac-global-color-primary-30);
  }
`;

const toolIconWrapperCSS = (color: string) => css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: ${color}15;
  color: ${color};
  font-size: 20px;
`;

const getPreciseMs = (iso: string) => {
    if (!iso) return 0;
    const date = new Date(iso);
    const match = iso.match(/\.(\d+)/);
    if (match) {
        const seconds = Math.floor(date.getTime() / 1000);
        const msFrac = parseFloat("0." + match[1]) * 1000;
        return seconds * 1000 + msFrac;
    }
    return date.getTime();
};

function ToolStatCards({ projectId, timeRange, fetchKey }: { projectId: string; timeRange: TimeRange, fetchKey: any }) {
    const scale = useTimeBinScale({ timeRange });
    const utcOffsetMinutes = useUTCOffsetMinutes();

    const data = useLazyLoadQuery<any>(
        graphql`
      query ProjectDashboardSummaryPageToolQuery($projectId: ID!, $timeRange: TimeRange!, $timeBinConfig: TimeBinConfig!, $filterCondition: String!) {
        project: node(id: $projectId) {
          ... on Project {
            spanCountTimeSeries(timeRange: $timeRange, timeBinConfig: $timeBinConfig, filterCondition: $filterCondition) {
              data { okCount errorCount unsetCount }
            }
            # Fetch spans for latency calculation to ensure tool-only scope
            spans(timeRange: $timeRange, filterCondition: $filterCondition, first: 1000) {
              edges {
                node {
                  latencyMs
                  startTime
                  endTime
                }
              }
            }
          }
        }
      }
    `,
        {
            projectId,
            timeRange: {
                start: timeRange.start.toISOString(),
                end: timeRange.end.toISOString()
            },
            timeBinConfig: { scale, utcOffsetMinutes },
            filterCondition: 'span_kind == "TOOL"',
        },
        { fetchPolicy: "network-only", fetchKey }
    );

    const timeSeriesData = data.project?.spanCountTimeSeries?.data ?? [];
    let total = 0;
    let failed = 0;

    timeSeriesData.forEach((d: any) => {
        total += (d.okCount + d.errorCount + d.unsetCount);
        failed += d.errorCount;
    });

    const spans = data.project?.spans?.edges ?? [];
    const avgLatency = useMemo(() => {
        if (spans.length === 0) return 0;
        const totalMs = spans.reduce((acc: number, e: any) => {
            const node = e.node;
            if (node?.startTime && node?.endTime) {
                return acc + (getPreciseMs(node.endTime) - getPreciseMs(node.startTime));
            }
            return acc + (node?.latencyMs ?? 0);
        }, 0);
        return totalMs / spans.length;
    }, [spans]);

    const successRate = total > 0 ? ((total - failed) / total) * 100 : 100;
    const latency = avgLatency;

    const fmtLatency = (ms: number | null | undefined, hasCalls: boolean) => {
        if (ms == null || (ms === 0 && !hasCalls)) return "--";
        return ms < 1000 ? `${ms.toFixed(2)}ms` : `${(ms / 1000).toFixed(2)}s`;
    };

    return (
        <div css={toolStatGridCSS}>
            <div css={toolStatCardCSS}>
                <div css={toolIconWrapperCSS("#3b82f6")}>🛠️</div>
                <div>
                    <div css={css`font-size: 20px; font-weight: 700; color: var(--ac-global-text-color-900); text-align: left;`}>{total.toLocaleString()}</div>
                    <div css={css`font-size: 11px; color: var(--ac-global-text-color-500); font-weight: 500;`}>Total Tool Calls</div>
                </div>
            </div>

            <div css={toolStatCardCSS}>
                <div css={toolIconWrapperCSS("#f59e0b")}>🕒</div>
                <div>
                    <div css={css`font-size: 20px; font-weight: 700; color: var(--ac-global-text-color-900); text-align: left;`}>{fmtLatency(latency, total > 0)}</div>
                    <div css={css`font-size: 11px; color: var(--ac-global-text-color-500); font-weight: 500;`}>Avg Latency</div>
                </div>
            </div>

            <div css={toolStatCardCSS}>
                <div css={toolIconWrapperCSS("#10b981")}>✅</div>
                <div>
                    <div css={css`font-size: 20px; font-weight: 700; color: var(--ac-global-text-color-900); text-align: left;`}>{successRate.toFixed(2)}%</div>
                    <div css={css`font-size: 11px; color: var(--ac-global-text-color-500); font-weight: 500;`}>Success Rate</div>
                </div>
            </div>

            <div css={toolStatCardCSS}>
                <div css={toolIconWrapperCSS("#f43f5e")}>❌</div>
                <div>
                    <div css={css`font-size: 20px; font-weight: 700; color: var(--ac-global-text-color-900); text-align: left;`}>{failed.toLocaleString()}</div>
                    <div css={css`font-size: 11px; color: var(--ac-global-text-color-500); font-weight: 500;`}>Failed Calls</div>
                </div>
            </div>
        </div>
    );
}

function RetrieverStatCards({ projectId, timeRange, fetchKey }: { projectId: string; timeRange: TimeRange, fetchKey: any }) {
    const scale = useTimeBinScale({ timeRange });
    const utcOffsetMinutes = useUTCOffsetMinutes();

    const data = useLazyLoadQuery<any>(
        graphql`
      query ProjectDashboardSummaryPageRetrieverQuery($projectId: ID!, $timeRange: TimeRange!, $timeBinConfig: TimeBinConfig!, $filterCondition: String!) {
        project: node(id: $projectId) {
          ... on Project {
            spanCountTimeSeries(timeRange: $timeRange, timeBinConfig: $timeBinConfig, filterCondition: $filterCondition) {
              data { okCount errorCount unsetCount }
            }
            # Fetch spans for latency calculation to ensure retriever-only scope
            spans(timeRange: $timeRange, filterCondition: $filterCondition, first: 1000) {
              edges {
                node {
                  latencyMs
                  startTime
                  endTime
                }
              }
            }
          }
        }
      }
    `,
        {
            projectId,
            timeRange: {
                start: timeRange.start.toISOString(),
                end: timeRange.end.toISOString()
            },
            timeBinConfig: { scale, utcOffsetMinutes },
            filterCondition: 'span_kind == "RETRIEVER"',
        },
        { fetchPolicy: "network-only", fetchKey }
    );

    const timeSeriesData = data.project?.spanCountTimeSeries?.data ?? [];
    let total = 0;
    let failed = 0;

    timeSeriesData.forEach((d: any) => {
        total += (d.okCount + d.errorCount + d.unsetCount);
        failed += d.errorCount;
    });

    const spans = data.project?.spans?.edges ?? [];
    const avgLatency = useMemo(() => {
        if (spans.length === 0) return 0;
        const totalMs = spans.reduce((acc: number, e: any) => {
            const node = e.node;
            if (node?.startTime && node?.endTime) {
                return acc + (getPreciseMs(node.endTime) - getPreciseMs(node.startTime));
            }
            return acc + (node?.latencyMs ?? 0);
        }, 0);
        return totalMs / spans.length;
    }, [spans]);

    const successRate = total > 0 ? ((total - failed) / total) * 100 : 100;
    const latency = avgLatency;

    const fmtLatency = (ms: number | null | undefined, hasCalls: boolean) => {
        if (ms == null || (ms === 0 && !hasCalls)) return "--";
        return ms < 1000 ? `${ms.toFixed(2)}ms` : `${(ms / 1000).toFixed(2)}s`;
    };

    return (
        <div css={toolStatGridCSS}>
            <div css={toolStatCardCSS}>
                <div css={toolIconWrapperCSS("#3b82f6")}>🔍</div>
                <div>
                    <div css={css`font-size: 20px; font-weight: 700; color: var(--ac-global-text-color-900); text-align: left;`}>{total.toLocaleString()}</div>
                    <div css={css`font-size: 11px; color: var(--ac-global-text-color-500); font-weight: 500;`}>Total Retrievals</div>
                </div>
            </div>

            <div css={toolStatCardCSS}>
                <div css={toolIconWrapperCSS("#f59e0b")}>🕒</div>
                <div>
                    <div css={css`font-size: 20px; font-weight: 700; color: var(--ac-global-text-color-900); text-align: left;`}>{fmtLatency(latency, total > 0)}</div>
                    <div css={css`font-size: 11px; color: var(--ac-global-text-color-500); font-weight: 500;`}>Avg Latency</div>
                </div>
            </div>

            <div css={toolStatCardCSS}>
                <div css={toolIconWrapperCSS("#10b981")}>✅</div>
                <div>
                    <div css={css`font-size: 20px; font-weight: 700; color: var(--ac-global-text-color-900); text-align: left;`}>{successRate.toFixed(2)}%</div>
                    <div css={css`font-size: 11px; color: var(--ac-global-text-color-500); font-weight: 500;`}>Success Rate</div>
                </div>
            </div>

            <div css={toolStatCardCSS}>
                <div css={toolIconWrapperCSS("#f43f5e")}>❌</div>
                <div>
                    <div css={css`font-size: 20px; font-weight: 700; color: var(--ac-global-text-color-900); text-align: left;`}>{failed.toLocaleString()}</div>
                    <div css={css`font-size: 11px; color: var(--ac-global-text-color-500); font-weight: 500;`}>Failed Calls</div>
                </div>
            </div>
        </div>
    );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export function ProjectDashboardSummaryPage() {
    const { projectId } = useParams();
    if (!projectId) throw new Error("projectId is required");

    const { timeRange: contextTimeRange } = useTimeRange();
    const { fetchKey } = useStreamState();

    const timeRange = useMemo<TimeRange>(() => {
        const start = contextTimeRange.start;
        const end = contextTimeRange.end;
        // If relative (null start/end), we provide fallback for charts that need it,
        // but try to keep it as close to "now" as possible to match context.
        if (start && end) return { start, end };
        if (!start && end) return { start: new Date(end.getTime() - ONE_MONTH_MS), end };
        if (start && !end) return { start, end: new Date(start.getTime() + ONE_MONTH_MS) };

        const now = new Date();
        return { start: new Date(now.getTime() - ONE_MONTH_MS), end: now };
    }, [contextTimeRange]);

    // Widget order + chart type state
    const [widgets, setWidgets] = useState<WidgetMeta[]>(INITIAL_WIDGETS);
    const [activeSection, setActiveSection] = useState<"usage" | "quality" | "tools" | "retriever" | "privacy">("usage");



    // CSV export handlers
    const [downloadHandlers, setDownloadHandlers] = useState<Record<string, () => void>>({});

    // CSV data store for global export
    const [allData, setAllData] = useState<Record<string, any[]>>({});

    const handleDataExport = useCallback((id: string, title: string, data: any[]) => {
        setAllData(prev => ({ ...prev, [id]: data }));
        setDownloadHandlers((prev: Record<string, () => void>) => ({
            ...prev,
            [id]: () => downloadCSV(data, `${title.toLowerCase().replace(/\s+/g, "_")}.csv`),
        }));
    }, []);

    const handleExportGlobal = useCallback(() => {
        // Collect all data into a single array for a "Full Report"
        // This is a bit naive but satisfies the "one click" requirement
        const report: any[] = [];
        Object.entries(allData).forEach(([id, data]) => {
            (data as any[]).forEach(row => {
                report.push({ Widget: id, ...row });
            });
        });
        if (report.length > 0) {
            downloadCSV(report, `full_dashboard_report_${new Date().getTime()}.csv`);
        }
    }, [allData]);

    const handleChartTypeChange = useCallback((id: WidgetId, type: ChartType) => {
        setWidgets((prev) => prev.map((w) => w.id === id ? { ...w, chartType: type } : w));
    }, []);

    // DnD sensors
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
    );

    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            setWidgets((prev) => {
                const oldIndex = prev.findIndex((w) => w.id === active.id);
                const newIndex = prev.findIndex((w) => w.id === over.id);
                return arrayMove(prev, oldIndex, newIndex);
            });
        }
    }, []);

    return (
        <main css={pageCSS}>
            {/* Header */}
            <div css={headerCSS}>
                <div>
                    <div css={titleCSS}>📊 Dashboard</div>
                    <div css={subtitleCSS}>
                        Real-time observability · Drag widgets to reorder · Hover a widget to change chart type or download CSV
                    </div>
                </div>
                <Flex direction="row" alignItems="center" gap="size-150">
                    <ConnectedTimeRangeSelector size="M" />
                    <button
                        css={css`
        /* Sandalwood Color Palette */
        background: #E6D5B8; /* Light Sandal base */
        color: #4A3728;      /* Deep Earthy Brown for text contrast */
        border: 1px solid #D2B48C;
        
        padding: 10px 20px;
        border-radius: 10px;
        font-weight: 700;
        font-size: 13px;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 10px;
        
        /* Soft, organic shadow */
        box-shadow: 0 4px 10px rgba(74, 55, 40, 0.12);
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        
        &:hover {
            background: #D2B48C; /* Slightly darker on hover */
            transform: translateY(-2px);
            box-shadow: 0 6px 18px rgba(74, 55, 40, 0.18);
        }
        
        &:active {
            transform: translateY(0);
            background: #C1A17B;
        }

        /* Icon styling */
        span {
            font-size: 16px;
        }
    `}
                        onClick={handleExportGlobal}
                    >
                        <span>📥</span> Download Full Report
                    </button>
                </Flex>
            </div>

            {/* Stat Cards */}
            <ErrorBoundary>
                <Suspense fallback={
                    <div css={css`display: flex; align-items: center; padding: 24px;`}>
                        <Loading />
                    </div>
                }>
                    <DashboardStatCards projectId={projectId} timeRange={timeRange} fetchKey={fetchKey} />
                </Suspense>
            </ErrorBoundary>

            {/* Section Tabs */}
            <div css={sectionHeaderCSS}>
                <div css={sectionTabCSS(activeSection === "usage")} onClick={() => setActiveSection("usage")}>Usage</div>
                <div css={sectionTabCSS(activeSection === "quality")} onClick={() => setActiveSection("quality")}>Quality</div>
                <div css={sectionTabCSS(activeSection === "privacy")} onClick={() => setActiveSection("privacy")}>Privacy</div>
                <div css={sectionTabCSS(activeSection === "tools")} onClick={() => setActiveSection("tools")}>Tool calls</div>
                <div css={sectionTabCSS(activeSection === "retriever")} onClick={() => setActiveSection("retriever")}>Retriever</div>
            </div>

            {/* Content Based on Section */}
            {activeSection === "usage" && (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={widgets.map((w) => w.id)}
                        strategy={rectSortingStrategy}
                    >
                        <div css={widgetGridCSS}>
                            {widgets.map((widget) => (
                                <DashboardWidget
                                    key={widget.id}
                                    id={widget.id}
                                    title={widget.title}
                                    subtitle={widget.subtitle}
                                    chartType={widget.chartType}
                                    supportsTypeSwitch={widget.supportsTypeSwitch}
                                    onChartTypeChange={(type) => handleChartTypeChange(widget.id, type)}
                                    onDownloadCSV={downloadHandlers[widget.id]}
                                    info={widget.info}
                                >
                                    <ErrorBoundary>
                                        <Suspense fallback={<Loading />}>
                                            <WidgetContent
                                                widgetId={widget.id}
                                                widgetTitle={widget.title}
                                                chartType={widget.chartType}
                                                projectId={projectId}
                                                timeRange={timeRange}
                                                onDataExport={handleDataExport}
                                                fetchKey={fetchKey}
                                            />
                                        </Suspense>
                                    </ErrorBoundary>
                                </DashboardWidget>
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            )}

            {activeSection === "quality" && (
                <div css={css`display: flex; flex-direction: column; gap: 24px;`}>
                    <ErrorBoundary>
                        <Suspense fallback={
                            <div css={css`display: flex; align-items: center; padding: 24px;`}>
                                <Loading />
                            </div>
                        }>
                            <QualityDashboard projectId={projectId} timeRange={timeRange} fetchKey={fetchKey} onDataExport={handleDataExport} />
                        </Suspense>
                    </ErrorBoundary>
                </div>
            )}

            {activeSection === "privacy" && (
                <div css={css`display: flex; flex-direction: column; gap: 24px;`}>
                    <div css={widgetGridCSS}>
                        <DashboardWidget
                            id="piiUsage"
                            title="🛡️ PII Detections"
                            subtitle="Trends of personally identifiable information redacted"
                            chartType="line"
                            supportsTypeSwitch={false}
                            onDownloadCSV={downloadHandlers["piiUsage"]}
                            info="Visualizes the number of spans where PII was detected and redacted."
                        >
                            <ErrorBoundary>
                                <Suspense fallback={<Loading />}>
                                    <PIIUsageSummary
                                        projectId={projectId}
                                        timeRange={timeRange}
                                        fetchKey={fetchKey}
                                        widgetId="piiUsage"
                                        widgetTitle="PII Detections"
                                        onDataExport={handleDataExport}
                                    />
                                </Suspense>
                            </ErrorBoundary>
                        </DashboardWidget>
                    </div>
                </div>
            )}

            {activeSection === "retriever" && (
                <div css={css`display: flex; flex-direction: column; gap: 24px;`}>
                    <ErrorBoundary>
                        <Suspense fallback={
                            <div css={toolStatGridCSS}>
                                {[1, 2, 3, 4].map(i => <div key={i} css={toolStatCardCSS} style={{ opacity: 0.5, height: 75 }}><Loading /></div>)}
                            </div>
                        }>
                            <RetrieverStatCards projectId={projectId} timeRange={timeRange} fetchKey={fetchKey} />
                        </Suspense>
                    </ErrorBoundary>

                    <ErrorBoundary>
                        <Suspense fallback={<Loading />}>
                            <RetrieverPerformanceTable projectId={projectId} timeRange={timeRange} fetchKey={fetchKey} onDataExport={handleDataExport} />
                        </Suspense>
                    </ErrorBoundary>

                    <div css={widgetGridCSS}>
                        <DashboardWidget
                            id="retrieverUsageOverTime"
                            title="📈 Retriever Usage Over Time"
                            subtitle="Document retrieval volume trends"
                            chartType="line"
                            supportsTypeSwitch={false}
                            onDownloadCSV={downloadHandlers["retrieverUsageOverTime"]}
                            info="Shows the number of retrieval calls made."
                        >
                            <ErrorBoundary>
                                <Suspense fallback={<Loading />}>
                                    <RetrieverUsageSummary projectId={projectId} timeRange={timeRange} fetchKey={fetchKey} widgetId="retrieverUsageOverTime" widgetTitle="Retriever Usage Over Time" onDataExport={handleDataExport} />
                                </Suspense>
                            </ErrorBoundary>
                        </DashboardWidget>
                    </div>
                </div>
            )}

            {activeSection === "tools" && (
                <div css={css`display: flex; flex-direction: column; gap: 24px;`}>
                    <ErrorBoundary>
                        <Suspense fallback={
                            <div css={toolStatGridCSS}>
                                {[1, 2, 3, 4].map(i => <div key={i} css={toolStatCardCSS} style={{ opacity: 0.5, height: 75 }}><Loading /></div>)}
                            </div>
                        }>
                            <ToolStatCards projectId={projectId} timeRange={timeRange} fetchKey={fetchKey} />
                        </Suspense>
                    </ErrorBoundary>

                    <ErrorBoundary>
                        <Suspense fallback={<Loading />}>
                            <ToolPerformanceTable projectId={projectId} timeRange={timeRange} fetchKey={fetchKey} onDataExport={handleDataExport} />
                        </Suspense>
                    </ErrorBoundary>

                    <div css={widgetGridCSS}>
                        <DashboardWidget
                            id="toolUsageOverTime"
                            title="📈 Tool Usage Over Time"
                            subtitle="Tool call distribution trends"
                            chartType="line"
                            supportsTypeSwitch={false}
                            onChartTypeChange={() => { }}
                            onDownloadCSV={downloadHandlers["toolUsageOverTime"]}
                            info="Visualizes how many times specific tools were called across the timeline."
                        >
                            <ErrorBoundary>
                                <Suspense fallback={<Loading />}>
                                    <ToolUsageSummary projectId={projectId} timeRange={timeRange} fetchKey={fetchKey} widgetId="toolUsageOverTime" widgetTitle="Tool Usage Over Time" onDataExport={handleDataExport} />
                                </Suspense>
                            </ErrorBoundary>
                        </DashboardWidget>

                        <DashboardWidget
                            id="toolLatencyComparison"
                            title="⚡ Latency Comparison"
                            subtitle="Response time metrics per tool"
                            chartType="line"
                            supportsTypeSwitch={false}
                            onChartTypeChange={() => { }}
                            onDownloadCSV={downloadHandlers["toolLatencyComparison"]}
                            info="Compares the average response time (latency) of different tools over time."
                        >
                            <ErrorBoundary>
                                <Suspense fallback={<Loading />}>
                                    <ToolLatencySummary projectId={projectId} timeRange={timeRange} fetchKey={fetchKey} widgetId="toolLatencyComparison" widgetTitle="Latency Comparison" onDataExport={handleDataExport} />
                                </Suspense>
                            </ErrorBoundary>
                        </DashboardWidget>
                    </div>
                </div>
            )}
        </main>
    );
}
